/* global Vue, VueI18n VueRouter, primevue, AZURE_URI, BOOKING_APP_URI, LOGO, HOME_TITLE, HOME_URL, LOCALE, DESTINATIONS, DESTINATION, PROPERTY_PHOBS_ID, PREFIX, DATA_LAYER, PROPERTIES_URL, AlgoliaEvents */

import { Notifications, useState, setErrorAndStopLoading, stopLoading, startLoading } from './components/Notifications'
import BookingCalendar from './components/BookingCalendar'
import Guests from './components/Guests'
import AccessCode from './components/AccessCode'
import CldImg from './components/CldImg'
import { AvailabilityCalendar } from './../classes/AvailabilityCalendar'
import { messagesSpecific } from '../i18n/translations'
import { CreateI18n } from '../i18n/create-i18n'
import { BookingParamsWithFilters } from '../classes/BookingParams'
import AuthService from '../AuthService'
import { formatDateForMaistra, localizeCalendar } from '../utils-common'
import { useCurrencyObserver } from '../use-currency-observer'

const i18n = CreateI18n(messagesSpecific)

const CheckRatesApp = Vue.createApp({
  name: 'CheckRatesApp',
  components: { Notifications, BookingCalendar, Guests, AccessCode, CldImg },
  template: `
<Notifications />
<transition name="fade">
<div v-if="showCheckRates" class="check-rates">
  <header class="header header--simple">
    <div class="header__top">
      <div class="header-logos">
        <a :href="HOME_URL" :title="HOME_TITLE" class="header-logos__maistra">
          <picture>
            <source media="(min-width: 992px)" :srcset="LOGO.desktop">
            <img :src="LOGO.mobile" :alt="HOME_TITLE" width="48" height="48">
          </picture>
        </a>
      </div>
      <div class="header__icons">
        <button class="button button--blank" @click="hideModal()" :title="$t('checkRates.closeButtonLabel')">
          <span class="hide-sm-down">{{ $t('checkRates.closeButtonLabel') }}</span>
          <i class="icon-close"></i>
        </button>
      </div>
    </div>
  </header>

  <div
    v-show="step === 3 || step === 4"
    id="modal-footer-teleport"
    class="modal__footer">
  </div>

  <div
    v-if="!state.isLoading.value && !state.fatalError.value"
    class="check-rates__main container container--fluid"
    :class="{'check-rates__main--padding': step > 2}">

    <div class="main-title">
      <button
        v-if="step !== 1"
        @click="changeStep(step -1)"
        class="main-title__back">
        <i class="icon-arrow-left-long"></i>
        <span>{{ $t('checkRates.backLabel') }}</span>
      </button>
      <div class="main-title__text">
        <span class="main-title__number">{{ step }}</span>
        <h2 class="no-margin">{{ $t('checkRates.stepTitle' + step) }}</h2>
      </div>
    </div>

    <div v-if="step === 1" class="grid">
      <div
        role="button"
        v-for="destination in destinationsWithProperties"
        :disabled="destination.unavailableForAccessCode"
        @click="bookingParams.destination = destination; nextStep()"
        class="col-12 col-xs-10 col-sm-8 col-md-6 col-lg-5 col-xxl-2 check-rates__destination">
        <h4 class="h2">{{ destination.title }}</h4>
        <div>
          <CldImg :src="destination.image" :alt="destination.title" />
        </div>
      </div>

      <AccessCode
        class="col-12 col-xs-10 col-sm-8 col-md-6 col-lg-5 col-xxl-3 order-xxl-11"
        v-model="bookingParams.accessCode"
        @validPropertyIds="validPropertyIds =  $event"
        @error="setErrorAndStopLoading" />

      <button
        @click="bookingParams.destination = {title: $t('all'), slug: $t('all').toLowerCase()}; nextStep()"
        class="col-12 col-lg-5 col-xxl-3 offset-xxl-9 order-xxl-10 check-rates__select-all negative-cp-lg-down">
        <span class="h3 no-margin">{{ $t('checkRates.allDestinations') }}</span>
      </button>
    </div>

    <div v-if="step === 2">
      <div class="grid">
        <div class="col-12 col-xs-11 col-sm-10 col-md-9 col-lg-8 col-xl-4 offset-xxl-8 order-xl-3">
          <div class="check-rates__image">
            <CldImg
              v-if="bookingParams.destination.slug === $t('all').toLowerCase()"
              src="https://res.cloudinary.com/maistra/image/upload/Destinations/Rovinj/Rovinj%20-TZ/tommueller-_rovinj2018-001_pldkdm.jpg"
              :alt="$t('all')" />
            <CldImg
              v-else
              :src="bookingParams.destination.image"
              :alt="bookingParams.destination.title" />
            <CldImg
              v-for="property in propertiesByMenuGroup('hotels, resorts, camping')"
              v-show="property.title === hoverPropertyTitle"
              :src="property.image"
              :alt="property.title" />
          </div>
        </div>

        <div class="check-rates__property-list col-12 col-sm-6 col-xl-4 col-xxl-3 order-xl-1">
          <h3>{{ $t('checkRates.hotelsResorts') }}</h3>
          <p v-if="propertiesByMenuGroup('hotels, resorts').length === 0" class="no-margin-top">{{ $t('checkRates.noProperties') }}</p>

          <a
            v-for="property in propertiesByMenuGroup('hotels, resorts')"
            class="check-rates__property-title"
            :disabled="!(property.priceFrom && property.priceFrom > 0) || property.unavailableForAccessCode"
            @click="selectProperty(property)"
            @mouseover="hoverPropertyTitle = property.title"
            @mouseout="hoverPropertyTitle = ''">
            <span>{{ property.title }}</span>
            <span v-if="property.stars" class="stars" :class="'theme--' + property.brand">
              <i v-for="n in property.stars" class="icon-star"></i>
            </span>
          </a>

          <button
            @click="selectProperty(null)"
            class="check-rates__select-all check-rates__select-all--properties negative-cp-lg-down">
            <span class="h3 no-margin">
              {{ $t('checkRates.allProperties') }} {{ bookingParams.destination.slug == 'all' ? $t('allDestinations') : bookingParams.destination.title }}
            </span>
          </button>
        </div>

        <div class="check-rates__property-list col-12 col-sm-6 col-xl-4 col-xxl-3 offset-xxl-4 order-xl-2">
          <h3>{{ $t('checkRates.campsites') }}</h3>
          <p v-if="propertiesByMenuGroup('camping').length === 0" class="no-margin-top">{{ $t('checkRates.noProperties') }}</p>

          <a
            v-for="property in propertiesByMenuGroup('camping')"
            class="check-rates__property-title"
            :disabled="!(property.priceFrom && property.priceFrom > 0) || property.unavailableForAccessCode"
            @click="bookingParams.property = property; nextStep()"
            @mouseover="hoverPropertyTitle = property.title"
            @mouseout="hoverPropertyTitle = ''">
            <span>{{ property.title }}</span>
            <span v-if="property.stars" class="stars" :class="'theme--' + property.brand">
              <i v-for="n in property.stars" class="icon-star"></i>
            </span>
          </a>

          <AccessCode
            class="access-code--property"
            v-model="bookingParams.accessCode"
            @validPropertyIds="validPropertyIds =  $event"
            @error="setErrorAndStopLoading" />
        </div>
      </div>
    </div>

    <div v-if="step === 3" class="check-rates__calendar-container">
      <BookingCalendar
        v-if="!availabilityCalendar.loading"
        :startDate="bookingParams.startDate"
        :nights="bookingParams.nights"
        :propertyIds="propertyIds"
        :accessCode="bookingParams.accessCode"
        :availabilityCalendar="availabilityCalendar"
        @error="setErrorAndStopLoading"
        @dateSelected="bookingParams.startDate = $event.startDate; bookingParams.nights = $event.nights; nextStep()" />
    </div>

    <Guests
      v-if="step === 4"
      :adults="bookingParams.adults"
      :children="bookingParams.children"
      :buttonLabel="bookingParams.property ? $t('bookNow') : $t('checkRates.checkRatesButton')"
      :property="bookingParams.property"
      @updateGuests="updateGuests"/>

    <div v-if="step > 2" class="reservation-summary reservation-summary--search" :class="openWindow">
      <div class="reservation-summary__title">
        <h4>{{ $t('checkRates.serachOverview') }}</h4>
        <span @click="isOpen = !isOpen" role="button">
          {{ isOpen ? $t('bookingModal.closeBtn') : $t('bookingModal.openBtn') }}
        </span>
      </div>

      <div class="reservation-summary__content">
        <div>
          <div v-if="bookingParams.destination" class="reservation-summary__item">
            {{ $t('checkRates.destination') }} {{ bookingParams.destination.title }}
            <span role="button" @click="changeStep(1)">{{ $t('checkRates.edit') }}</span>
          </div>

          <div v-if="bookingParams.property" class="reservation-summary__item">
            <span class="font-maistra-display uppercase">
              {{ bookingParams.property.title }}
              <span> &nbsp; </span>
              <br class="hide-md-down hide-xl-up">
              <span class="stars stars--small" :class="'theme--' + bookingParams.property.brand">
                <i v-for="n in bookingParams.property.stars" class="icon-star"></i>
              </span>
            </span>
            <span role="button" @click="changeStep(2)">{{ $t('checkRates.edit') }}</span>
          </div>
          <div v-else class="reservation-summary__item">
            {{ $t('checkRates.allPropertiesInDestination') }}
            <span role="button" @click="changeStep(2)">{{ $t('checkRates.edit') }}</span>
          </div>

          <div v-if="step === 4" class="reservation-summary__item">
           {{ bookingParams.displayDate }} / {{ bookingParams.nights }} {{ $t('checkRates.nights') }}
            <span role="button" @click="changeStep(3)">{{ $t('checkRates.edit') }}</span>
          </div>
        </div>

        <AccessCode
          class="access-code--summary"
          v-model="bookingParams.accessCode"
          @validPropertyIds="validPropertyIds =  $event"
          @error="error = $event"
          />
      </div>
    </div>
  </div>
</div>
</transition>
  `,
  setup () {
    const { state } = useState()
    const accountId = Vue.ref('')
    const properties = Vue.reactive([])
    const showCheckRates = Vue.ref(false)
    const step = Vue.ref(1)
    const isOpen = Vue.ref(false)
    const { t } = VueI18n.useI18n()
    const openWindow = Vue.computed(() => isOpen.value ? 'reservation-summary--open' : '')
    const bookingParams = Vue.reactive(new BookingParamsWithFilters(t('all')))
    const { accessCode } = Vue.toRefs(bookingParams)
    const validPropertyIds = Vue.ref([])
    const hoverPropertyTitle = Vue.ref('')
    const availabilityCalendar = Vue.reactive(new AvailabilityCalendar())

    const destinationsWithProperties = DESTINATIONS.filter(d => d.show_on_check_rates)

    const propertyIds = Vue.computed(() => {
      if (!bookingParams.property) {
        const destination = bookingParams.destination
        return properties
          .filter(property => (destination.title === t('all') || destination.slug === property.destination) && property.phobsId)
          .map(property => property.phobsId)
      }
      if (bookingParams.property.phobsId) {
        return [bookingParams.property.phobsId]
      }
      return []
    })

    Vue.watch(step, async (value) => {
      if (value === 3) {
        try {
          await availabilityCalendar.loadAvailability({ propertyIds: propertyIds.value, accessCode: bookingParams.accessCode })
        } catch (e) {
          setErrorAndStopLoading(e)
        }
      }
    })

    Vue.watch(validPropertyIds, () => {
      const code = accessCode.value
      properties.forEach(property => {
        property.unavailableForAccessCode = code !== '' && !validPropertyIds.value.includes(property.phobsId)
      })
      destinationsWithProperties.forEach(destination => {
        destination.unavailableForAccessCode = code !== '' && !properties.find(property => property.destination === destination.slug && !property.unavailableForAccessCode)
      })
      if (code !== '' && step.value >= 3 && validPropertyIds.value.filter(id => propertyIds.value.includes(id)).length === 0) {
        changeStep(1)
      }
    })

    function nextStep () {
      changeStep(step.value + 1)
    }

    function changeStep (target) {
      if (target === 4 && bookingParams.property && bookingParams.property.defaultNrAdults) {
        bookingParams.adults = bookingParams.property.defaultNrAdults
      }

      const newRoute = `/check-rates?step=${target}`
      if (newRoute !== router.currentRoute.value.fullPath) {
        router.push(newRoute)
      }
    }

    function selectProperty (property) {
      if (bookingParams.property?.phobsId !== property?.phobsId) {
        bookingParams.startDate = new Date()
        bookingParams.nights = 1
      }

      bookingParams.property = property
      nextStep()
    }

    async function determineFirstStep () {
      const step = withDestination() ? 2 : 1
      if (withProperty()) return 3
      return step
    }

    function withDestination () {
      if (typeof DESTINATION === 'undefined' || DESTINATION === '') {
        return null
      }
      const destination = DESTINATIONS.find(d => DESTINATION === d.slug)
      if (destination != null) {
        bookingParams.destination = destination
      }
      return destination
    }

    function withProperty () {
      if (typeof PROPERTY_PHOBS_ID === 'undefined' || PROPERTY_PHOBS_ID === '') {
        return null
      }
      const property = properties.find(p => PROPERTY_PHOBS_ID === p.phobsId)
      if (property != null) {
        bookingParams.property = property
      }
      return property
    }

    function parseQueryParams ({ propertyId, accessCode, adults, children }) {
      if (propertyId != null) {
        const property = properties.find(p => propertyId === p.phobsId)
        if (property != null) {
          bookingParams.property = property
          bookingParams.destination = DESTINATIONS.find(d => property.destination === d.slug)
        }
      }
      if (adults != null) {
        bookingParams.adults = parseInt(adults)
      }
      if (children != null) {
        bookingParams.children = children.split(',').map(age => ({ age: parseInt(age) }))
      }
      if (accessCode != null) {
        bookingParams.accessCode = accessCode
      }
    }

    function hideModal () {
      router.push('/')
    }

    function updateGuests (e) {
      bookingParams.adults = e.adults
      bookingParams.children = e.children
      if (bookingParams.urlString.includes('propertyId')) {
        if (AlgoliaEvents) {
          AlgoliaEvents.convertedFilters({
            eventName: 'Booking Intent',
            property: [bookingParams.property.groupTitle],
            brand: [bookingParams.property.brand],
            destination: [bookingParams.property.destination],
            propertyType: [bookingParams.property.menuGroup],
            campingType: bookingParams.property.campingType ? [bookingParams.property.campingType] : [],
            holidayType: bookingParams.property.holidayType ? [bookingParams.property.holidayType] : [],
          })
        }

        const { phobsId, groupTitle, brand, menuGroup, destination } = bookingParams.property
        DATA_LAYER.wwwBookNow({
          id: phobsId,
          name: groupTitle,
          brand,
          category: `${menuGroup}/${destination}`,
        })
        window.location = `${BOOKING_APP_URI}${PREFIX}/step1?${bookingParams.urlString}&accountId=${accountId.value}&currency=${useCurrencyObserver().value}`
      } else {
        window.location = `${PROPERTIES_URL}?${bookingParams.urlString}`
      }
    }

    function propertiesByMenuGroup (group) {
      return properties.filter(property => {
        const destination = bookingParams.destination
        const hasDestination = destination.title === t('all') || destination.slug === property.destination
        const hasMenuGroup = group.split(', ').some(g => g === property.menuGroup)
        return hasMenuGroup && hasDestination && !property.isPropertyBundle
      })
    }

    async function getProperties () {
      startLoading('getProperties')
      try {
        Object.assign(properties, null)
        const response = await fetch(`${AZURE_URI}GetProperties?accessCode=${bookingParams.accessCode}&locale=${LOCALE}`)
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        Object.assign(properties, await response.json())
        properties.sort((a, b) => (a.propertyListingWeight ?? Number.MAX_VALUE) - (b.propertyListingWeight ?? Number.MAX_VALUE))
        stopLoading('getProperties')
      } catch (err) {
        setErrorAndStopLoading(err)
      }
    }

    router.afterEach(async (route) => {
      if (route.name === 'None') {
        step.value = 1
        properties.length = 0
        showCheckRates.value = false
        document.body.classList.remove('overflow-hidden')
        return
      }

      if (properties.length === 0) await getProperties()

      if (route.query.step === undefined) {
        router.replace('/check-rates?step=' + (await determineFirstStep()))
        return
      }

      const urlSearchParams = new URLSearchParams(window.location.search)
      const params = Object.fromEntries(urlSearchParams.entries())

      parseQueryParams({ ...route.query, ...params })

      step.value = parseInt(route.query.step)
      showCheckRates.value = true

      document.body.classList.add('overflow-hidden')
      DATA_LAYER.wwwCheckRates({ stepNumber: step.value, bookingParams })
    })

    Vue.onMounted(() => {
      AuthService.on('authenticated', e => {
        accountId.value = e.detail.accountId
      })
    })

    return {
      state,
      setErrorAndStopLoading,
      showCheckRates,
      step,
      nextStep,
      changeStep,
      bookingParams,
      availabilityCalendar,
      isOpen,
      openWindow,
      hideModal,
      propertiesByMenuGroup,
      propertyIds,
      updateGuests,
      hoverPropertyTitle,
      getProperties,
      selectProperty,
      LOGO,
      HOME_TITLE,
      HOME_URL,
      destinationsWithProperties,
      formatDateForMaistra,
      validPropertyIds,
    }
  },
})

if (location.hash.startsWith('#/check-rates')) {
  location.hash = '#'
}

const appElement = document.getElementById('check-rates-app')

const router = VueRouter.createRouter({
  history: VueRouter.createWebHashHistory(),
  routes: [
    {
      path: '/check-rates',
      name: 'CheckRatesApp',
      component: CheckRatesApp,
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'None',
      component: CheckRatesApp,
    },
  ],
  scrollBehavior (to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    } else if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

if (appElement) {
  CheckRatesApp.use(i18n)
  CheckRatesApp.use(primevue, {
    locale: localizeCalendar(LOCALE),
  })
  CheckRatesApp.use(router)
  CheckRatesApp.mount(appElement)
}

export default CheckRatesApp
